/* Browser notifications.
 *
 * The bell polls, which is right for a number on screen but cannot tell
 * somebody about a reply when the tab is in the background or closed. This
 * does — the same FCM the phone app uses, through a service worker.
 *
 * ASKED FOR AT THE RIGHT MOMENT, WHICH IS NOT ON ARRIVAL. A permission prompt
 * on first load, before anyone knows what the app is, gets denied — and a
 * denial is close to permanent: the browser stops asking, and the only way back
 * is a settings panel most people will never find. So this is only ever called
 * from a deliberate action, and it asks once.
 *
 * NOT AVAILABLE EVERYWHERE, and that is fine. No service worker, no HTTPS, a
 * browser without the Push API, an iPhone that has not added the app to its
 * home screen: every one of those returns false and the bell carries on exactly
 * as before. Nothing here is load-bearing.
 *
 * The config below is public by design — it identifies the Firebase project and
 * authorises nothing. The private key that signs sends lives on the server.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

import { api } from './client'

const firebaseConfig = {
  apiKey: 'AIzaSyDXKKhN82ynVWXZfMrYMk7RL_ahChoNkys',
  authDomain: 'trueway-df01a.firebaseapp.com',
  projectId: 'trueway-df01a',
  storageBucket: 'trueway-df01a.firebasestorage.app',
  messagingSenderId: '899052627712',
  appId: '1:899052627712:web:aceda0a9757de1059135e5',
}

/** The public half of the web push key pair. Also not a secret. */
const VAPID_KEY =
  'BC5isj0nSu6P19KMUSi4UnKNtmXBUeFc5aNCYv3spv89L0rB0SDEM2U7u7jO7iHlNWcBnCB0P5fmbhfmMAUiUFs'

let app: FirebaseApp | null = null

function firebase(): FirebaseApp {
  app ??= initializeApp(firebaseConfig)
  return app
}

/** Whether asking is worth it: supported here, and not already decided. */
export async function canAskForPush(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission !== 'default') return false
  return isSupported().catch(() => false)
}

/** Whether this browser is already set up to receive. */
export function pushGranted(): boolean {
  return 'Notification' in window && Notification.permission === 'granted'
}

/**
 * Ask, then tell the server where to push.
 *
 * Returns false for every kind of "no" — declined, unsupported, no service
 * worker — because the caller does the same thing in all of them: nothing.
 */
export async function enablePush(): Promise<boolean> {
  try {
    if (!(await isSupported())) return false

    const permission = pushGranted()
      ? 'granted'
      : await Notification.requestPermission()
    if (permission !== 'granted') return false

    // AT THE ROOT, not under BASE_URL. A service worker can only control pages
    // at or below its own path, and the deployed app serves from the domain
    // root — a worker registered under a base path would control nothing.
    // Vite copies public/ to the root of the build either way.
    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
      { scope: '/' },
    )

    const token = await getToken(getMessaging(firebase()), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
    if (!token) return false

    await api.post('/notifications/devices', {
      fcm_token: token,
      platform: 'web',
    })
    return true
  } catch (err) {
    // A blocked service worker, a browser that lies about support, an offline
    // registration: none of these should reach the user as an error. The bell
    // still works.
    console.debug('push: not enabled', err)
    return false
  }
}

/**
 * Messages arriving while the tab is in focus.
 *
 * The service worker draws nothing in this case — the browser assumes an app in
 * front of somebody will handle it itself. Here that means refreshing the bell,
 * so the count moves the moment something lands rather than at the next poll.
 */
export function onPushInForeground(handler: () => void): () => void {
  let stop = () => {}
  isSupported()
    .then((ok) => {
      if (!ok) return
      stop = onMessage(getMessaging(firebase()), () => handler())
    })
    .catch(() => {})
  return () => stop()
}
